import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { LOCAL_DATETIME_FMT, formatDate } from 'lib/helpers/dateUtils';
import { useGetUserTrainings } from 'lib/hooks/queries/trainings/useGetTrainings';
import { useGetWizardDelegatedTasks } from 'lib/hooks/queries/wizard/useGetWizardDelegatedTasks';

import { currentPractice } from 'components/practice/practiceState';

import { findMeetingForStep } from './config';

const formatTrainingDateTime = (startsAt, timezone) => {
  if (!startsAt) return 'Unset';
  const formatted = formatDate(startsAt, timezone, LOCAL_DATETIME_FMT);
  return formatted || 'Unset';
};

/**
 * Hook that consolidates all data fetching for a live training step.
 * Returns training meeting data, formatted date/time, attendees, and the link.
 */
const useLiveTrainingData = ({ orchestratorData, wizardState }) => {
  const { practiceId } = useOutletContext();
  const practice = useRecoilValue(currentPractice);
  const timezone = practice?.timezone;

  const { data: trainingsData } = useGetUserTrainings({
    params: { practiceId: Number(practiceId) },
    dependencies: [practiceId],
    options: { enabled: !!practiceId, refetchOnMount: true }
  });

  const instanceId = wizardState?.instance?.id;

  const { data: taskDelegationData } = useGetWizardDelegatedTasks({
    params: {
      wizard_instance_id: instanceId,
      get_all_delegations: true,
      practiceId: wizardState?.instance?.instance_id,
      limit: 100
    },
    options: { enabled: Boolean(instanceId) }
  });

  const training = useMemo(
    () =>
      findMeetingForStep(trainingsData?.meetings, orchestratorData?.stepCode, wizardState?.steps),
    [trainingsData?.meetings, orchestratorData?.stepCode, wizardState?.steps]
  );

  const formattedDateTime = useMemo(
    () => formatTrainingDateTime(training?.starts_at, timezone),
    [training?.starts_at, timezone]
  );

  const attendees = useMemo(() => {
    const delegations = taskDelegationData?.data?.delegations;
    if (!delegations) return [];

    const stepAssignments =
      delegations.steps?.find((s) => s.step_id === orchestratorData?.stepId)?.assignments || [];
    const substepAssignments =
      delegations.substeps?.find((s) => s.substep_id === orchestratorData?.substepId)
        ?.assignments || [];

    return [
      ...stepAssignments,
      ...substepAssignments.filter(
        (sub) => !stepAssignments.some((step) => step.user_id === sub.user_id)
      )
    ];
  }, [taskDelegationData, orchestratorData?.stepId, orchestratorData?.substepId]);

  return {
    trainingId: training?.id || null,
    practiceId: practiceId ? Number(practiceId) : null,
    formattedDateTime,
    trainingLink: training?.meeting_link || null,
    attendees,
    currentAttendanceStatus: training?.attendance_status || null,
    currentTrainingConfirmations: training?.training_confirmations || null
  };
};

export default useLiveTrainingData;
